import { __rest } from 'tslib';
import { jsxs, jsx } from 'react/jsx-runtime';
import clsx from 'clsx';
import { TreeSelect } from 'primereact/treeselect';
import { getPlaceholder } from '../../../_utils/get-placeholder.js';
import { createOverflowItemTemplate } from '../../../_utils/OverflowTemplate.utils.js';
import { resolveOverflowPanelProps } from '../../../_utils/overlay-panel.utils.js';
import { isPlainLabelTree, getLabelText } from '../../../_utils/plain-label.utils.js';
import { TreeSelectSelectedPills } from './TreeSelectSelectedPills.js';
import { useTreeSelectSelectionModel } from '../hooks/useTreeSelectSelectionModel.js';

function TreeSelectInput(_a) {
  var _b;
  var {
      forwardedRef
    } = _a,
    props = __rest(_a, ["forwardedRef"]);
  const shouldUseOverflowNodeTemplate = !props.nodeTemplate && isPlainLabelTree(props.options, node => getLabelText(node.label), node => node.children);
  const effectiveSelectionMode = (_b = props.selectionMode) !== null && _b !== void 0 ? _b : 'single';
  const placeholder = getPlaceholder(Object.assign(Object.assign({}, props), {
    isDropdown: true
  }));
  const effectivePlaceholder = placeholder || (effectiveSelectionMode === 'multiple' || effectiveSelectionMode === 'checkbox' ? 'Select items' : 'Select item');
  const {
    display,
    handleChange,
    handleDismissSelectedValue,
    handleNodeSelect,
    handleNodeUnselect,
    selectedNodes,
    shouldRenderSelectedPills,
    value,
    valueTemplate
  } = useTreeSelectSelectionModel(Object.assign(Object.assign({}, props), {
    placeholder: effectivePlaceholder,
    selectionMode: effectiveSelectionMode
  }));
  const {
      checkboxSelectionBehavior: _checkboxSelectionBehavior,
      display: _display,
      onChange: _onChange,
      onNodeSelect: _onNodeSelect,
      onNodeUnselect: _onNodeUnselect,
      options,
      bulkMode: _bulkMode,
      errorClassName: _errorClassName,
      errorMessage: _errorMessage,
      focus: _focus,
      hint: _hint,
      horizontal: _horizontal,
      inputClassName: _inputClassName,
      label: _label,
      labelClassName: _labelClassName,
      labelHidden: _labelHidden,
      labelTooltip: _labelTooltip,
      placeholder: _placeholder,
      selectedValueLayout: _selectedValueLayout,
      selectionMode: _selectionMode,
      suffix: _suffix,
      suppressRequiredLabel: _suppressRequiredLabel,
      value: _value,
      valueTemplate: _valueTemplate,
      wrapperClassName: _wrapperClassName,
      className: customClassName
    } = props,
    restProps = __rest(props, ["checkboxSelectionBehavior", "display", "onChange", "onNodeSelect", "onNodeUnselect", "options", "bulkMode", "errorClassName", "errorMessage", "focus", "hint", "horizontal", "inputClassName", "label", "labelClassName", "labelHidden", "labelTooltip", "placeholder", "selectedValueLayout", "selectionMode", "suffix", "suppressRequiredLabel", "value", "valueTemplate", "wrapperClassName", "className"]);
  const hasInvalidState = Boolean(props.invalid || props.errorMessage);
  const effectiveDisplay = display !== null && display !== void 0 ? display : 'comma';
  const {
    resolvedPanelClassName
  } = resolveOverflowPanelProps({
    panelClassName: restProps.panelClassName,
    shouldUseOverflow: shouldUseOverflowNodeTemplate
  });
  const nodeTemplate = createOverflowItemTemplate({
    enabled: shouldUseOverflowNodeTemplate,
    getLabel: node => getLabelText(node.label),
    className: 'p-treenode-label ics-overflow-label ics-overflow-label--flex',
    fallbackTemplate: props.nodeTemplate
  });
  return jsxs("div", {
    className: "ics-treeselect",
    children: [jsx(TreeSelect, Object.assign({}, restProps, {
      className: clsx(customClassName, hasInvalidState && 'p-invalid'),
      ref: forwardedRef,
      display: effectiveDisplay,
      options: options,
      placeholder: effectivePlaceholder,
      selectionMode: effectiveSelectionMode,
      invalid: hasInvalidState,
      value: value,
      appendTo: document.body,
      nodeTemplate: nodeTemplate,
      panelClassName: resolvedPanelClassName,
      valueTemplate: valueTemplate,
      onNodeSelect: handleNodeSelect,
      onNodeUnselect: handleNodeUnselect,
      onChange: handleChange
    })), shouldRenderSelectedPills ? jsx(TreeSelectSelectedPills, {
      selectedNodes: selectedNodes,
      onDismiss: handleDismissSelectedValue,
      disabled: props.disabled
    }) : null]
  });
}

export { TreeSelectInput };
